import { Box, IconButton } from "@mui/material"
import { DraftOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"

import { TransferRowActions } from "../components/TransferListPage/TransferRowActions"
import { TransferStatusWithSync } from "../components/TransferListPage/TransferStatusWithSync"
import { TransferTypeChip } from "../components/TransferTypeChip"

import type { Transfer } from "@/graphql/codegen/graphql"
import { formatDate } from "@/utils/date"
import { formatUserName } from "@/utils/user"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface UseTransferColumnsProps {
  isPrinting: boolean
  onEdit: (transfer: Transfer) => void
  onLpnEdit: (transfer: Transfer) => void
  onLaidDownCostEdit: (transfer: Transfer) => void
  onCancel: (transfer: Transfer) => void
  onReceivedConfirm: (transfer: Transfer) => void
  onWorkOrderConfirm: (transfer: Transfer) => void
  onWorkOrderPrint: (transfer: Transfer) => void
  onPickTicketPrint: (transfer: Transfer) => void
  onPickConfirm: (transfer: Transfer) => void
  onPalletLabelPrintAll: (transfer: Transfer) => void
  onDocumentsOpen: (transfer: Transfer) => void
}

export const useTransferColumns = ({
  isPrinting,
  onEdit,
  onLpnEdit,
  onLaidDownCostEdit,
  onCancel,
  onReceivedConfirm,
  onWorkOrderConfirm,
  onWorkOrderPrint,
  onPickTicketPrint,
  onPickConfirm,
  onPalletLabelPrintAll,
  onDocumentsOpen,
}: UseTransferColumnsProps): ColumnDef<Transfer>[] => [
  {
    accessorKey: "type",
    header: "Type",
    cell: ({ row }) => <TransferTypeChip type={row.original.transferType} />,
    enableSorting: true,
  },
  {
    accessorKey: "referenceCode",
    header: "Transfer #",
    enableSorting: false,
  },
  {
    accessorKey: "sourceWarehouse",
    header: "From",
    cell: ({ row }) => formatWarehouseLabel(row.original.sourceWarehouseArea),
    enableSorting: true,
  },
  {
    accessorKey: "targetWarehouse",
    header: "To",
    cell: ({ row }) => formatWarehouseLabel(row.original.targetWarehouseArea),
    enableSorting: true,
  },
  {
    accessorKey: "createdOn",
    header: "Created",
    cell: ({ row }) => formatDate(row.original.createdOn),
    enableSorting: true,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <TransferStatusWithSync transfer={row.original} />,
    enableSorting: true,
  },
  {
    accessorKey: "createdBy",
    header: "Created By",
    cell: ({ row }) => formatUserName(row.original.createdByUser, true),
    enableSorting: true,
  },
  {
    accessorKey: "productCostCompleted",
    header: "Product Cost Completed",
    // TODO(AK-6818): Show the respective check icon for inter-warehouse transfers once the backend exposes product cost completion.
    cell: () => "N/A",
    enableSorting: false,
  },
  {
    accessorKey: "docs",
    header: "",
    size: 56,
    enableSorting: false,
    cell: ({ row }) =>
      row.original.hasDocuments ? (
        <Box className='flex items-center justify-center'>
          <IconButton size='small' onClick={() => onDocumentsOpen(row.original)}>
            <DraftOutlined />
          </IconButton>
        </Box>
      ) : null,
  },
  {
    accessorKey: "actions",
    header: "",
    size: 112,
    enableSorting: false,
    cell: ({ row }) => (
      <Box className='flex items-center justify-end gap-x-2'>
        <TransferRowActions
          transfer={row.original}
          isPrinting={isPrinting}
          onEdit={onEdit}
          onLpnEdit={onLpnEdit}
          onLaidDownCostEdit={onLaidDownCostEdit}
          onReceivedConfirm={onReceivedConfirm}
          onCancel={onCancel}
          onWorkOrderConfirm={onWorkOrderConfirm}
          onWorkOrderPrint={onWorkOrderPrint}
          onPickTicketPrint={onPickTicketPrint}
          onPickConfirm={onPickConfirm}
          onPalletLabelPrintAll={onPalletLabelPrintAll}
        />
        <IconButton size='small' onClick={row.getToggleExpandedHandler()}>
          {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </IconButton>
      </Box>
    ),
  },
]
